import { Component, input, output, signal, computed, ChangeDetectionStrategy, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule, Copy, Check, ThumbsUp, ThumbsDown, Volume2, RefreshCw } from "lucide-angular";
import { CopilotTooltip } from "../directives/tooltip";
import { cn } from "../utils/utils";
import { injectChatLabels } from "../core/chat-labels";

@Component({
  standalone: true,
  selector: "button[agentChatAssistantMessageToolbarButton]",
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `<ng-content></ng-content>`,
  host: {
    "[class]": "computedClass()",
    "[attr.disabled]": "disabled() ? true : null",
    type: "button",
    "[attr.aria-label]": "title()",
  },
  hostDirectives: [
    {
      directive: CopilotTooltip,
      inputs: ["copilotTooltip: title", "tooltipPosition", "tooltipDelay"],
    },
  ],
})
export class AgentChatAssistantMessageToolbarButton {
  title = input<string>("");

  disabled = input<boolean>(false);

  inputClass = input<string | undefined>();

  computedClass = computed(() => cn("achat-msg-btn", this.inputClass()));
}

@Component({
  standalone: true,
  selector: "agent-chat-assistant-message-copy-button",
  imports: [CommonModule, LucideAngularModule, AgentChatAssistantMessageToolbarButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      agentChatAssistantMessageToolbarButton
      [title]="title() || labels.assistantMessageToolbarCopyMessageLabel"
      [disabled]="disabled()"
      [inputClass]="inputClass()"
      (click)="handleCopy($event)"
    >
      @if (copied()) {
      <lucide-angular [img]="CheckIcon" [size]="18"></lucide-angular>
      } @else {
      <lucide-angular [img]="CopyIcon" [size]="18"></lucide-angular>
      }
    </button>
  `,
})
export class AgentChatAssistantMessageCopyButton {
  readonly title = input<string | undefined>();

  readonly disabled = input<boolean>(false);

  readonly inputClass = input<string | undefined>();

  readonly content = input<string | undefined>();

  readonly clicked = output<void>();

  readonly CopyIcon = Copy;

  readonly CheckIcon = Check;

  readonly copied = signal(false);

  readonly labels = injectChatLabels();

  handleCopy(event?: Event): void {
    event?.stopPropagation();
    if (!this.content()) return;
    this.copied.set(true);
    setTimeout(() => this.copied.set(false), 2000);
    navigator.clipboard.writeText(this.content()!).then(
      () => this.clicked.emit(),
      (err) => {
        console.error("Failed to copy message:", err);
        this.copied.set(false);
      },
    );
  }
}

@Component({
  standalone: true,
  selector: "agent-chat-assistant-message-thumbs-up-button",
  imports: [CommonModule, LucideAngularModule, AgentChatAssistantMessageToolbarButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      agentChatAssistantMessageToolbarButton
      [title]="title() || labels.assistantMessageToolbarThumbsUpLabel"
      [disabled]="disabled()"
      [inputClass]="inputClass()"
      (click)="handleClick($event)"
    >
      <lucide-angular [img]="ThumbsUpIcon" [size]="18"></lucide-angular>
    </button>
  `,
})
export class AgentChatAssistantMessageThumbsUpButton {
  readonly title = input<string | undefined>();

  readonly disabled = input<boolean>(false);

  readonly inputClass = input<string | undefined>();

  readonly clicked = output<void>();

  readonly ThumbsUpIcon = ThumbsUp;

  readonly labels = injectChatLabels();

  handleClick(event?: Event): void {
    event?.stopPropagation();
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}

@Component({
  standalone: true,
  selector: "agent-chat-assistant-message-thumbs-down-button",
  imports: [CommonModule, LucideAngularModule, AgentChatAssistantMessageToolbarButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      agentChatAssistantMessageToolbarButton
      [title]="title() || labels.assistantMessageToolbarThumbsDownLabel"
      [disabled]="disabled()"
      [inputClass]="inputClass()"
      (click)="handleClick($event)"
    >
      <lucide-angular [img]="ThumbsDownIcon" [size]="18"></lucide-angular>
    </button>
  `,
})
export class AgentChatAssistantMessageThumbsDownButton {
  readonly title = input<string | undefined>();

  readonly disabled = input<boolean>(false);

  readonly inputClass = input<string | undefined>();

  readonly clicked = output<void>();

  readonly ThumbsDownIcon = ThumbsDown;

  readonly labels = injectChatLabels();

  handleClick(event?: Event): void {
    event?.stopPropagation();
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}

@Component({
  standalone: true,
  selector: "agent-chat-assistant-message-read-aloud-button",
  imports: [CommonModule, LucideAngularModule, AgentChatAssistantMessageToolbarButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      agentChatAssistantMessageToolbarButton
      [title]="title() || labels.assistantMessageToolbarReadAloudLabel"
      [disabled]="disabled()"
      [inputClass]="inputClass()"
      (click)="handleClick($event)"
    >
      <lucide-angular [img]="Volume2Icon" [size]="20"></lucide-angular>
    </button>
  `,
})
export class AgentChatAssistantMessageReadAloudButton {
  readonly title = input<string | undefined>();

  readonly disabled = input<boolean>(false);

  readonly inputClass = input<string | undefined>();

  readonly clicked = output<void>();

  readonly Volume2Icon = Volume2;

  readonly labels = injectChatLabels();

  handleClick(event?: Event): void {
    event?.stopPropagation();
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}

@Component({
  standalone: true,
  selector: "agent-chat-assistant-message-regenerate-button",
  imports: [CommonModule, LucideAngularModule, AgentChatAssistantMessageToolbarButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      agentChatAssistantMessageToolbarButton
      [title]="title() || labels.assistantMessageToolbarRegenerateLabel"
      [disabled]="disabled()"
      [inputClass]="inputClass()"
      (click)="handleClick($event)"
    >
      <lucide-angular [img]="RefreshCwIcon" [size]="18"></lucide-angular>
    </button>
  `,
})
export class AgentChatAssistantMessageRegenerateButton {
  readonly title = input<string | undefined>();

  readonly disabled = input<boolean>(false);

  readonly inputClass = input<string | undefined>();

  readonly clicked = output<void>();

  readonly RefreshCwIcon = RefreshCw;

  readonly labels = injectChatLabels();

  handleClick(event?: Event): void {
    event?.stopPropagation();
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}
